import { ChevronDown, LayoutGrid, RotateCw, Table2, Upload, X } from "lucide-react";
import FileSearchBox from "components/project/FileSearchBox";
import { FILE_LIST_COLUMNS, MIN_FILE_LIST_COLUMNS } from "lib/utils/fileListColumns";
import { FILE_SORT_PREF } from "lib/utils/fileSortpref";
import "css/fileListControls.css";

const SORT_LABEL_KEYS = {
  uploaded_at: "uploadDate",
  file_name: "fileName",
  is_corrupted: "column.fileStatus",
  file_type: "column.fileType",
  building_name: "column.buildingName",
  floor: "column.floor",
  location: "column.location",
  facility_type: "column.facilityType",
  facility_name: "column.facilityName",
};

export default function ProjectFileListControls({
  t,
  loading,
  error,
  totalCount,
  scopeLabel,
  detailFilterDropdown,
  searchCategoryOptions,
  searchCategory,
  searchKeyword,
  onSearchChange,
  sortPref,
  onSortChange,
  onRefresh,
  viewMode,
  onViewModeChange,
  visibleColumns,
  onToggleColumn,
  isUploadPanelOpen,
  uploadDisabled,
  uploadToggleLabel,
  onToggleUpload,
  uploadPanel,
  selectionActions,
}) {
  const text = (key, defaultValue, options = {}) =>
    t(`projectDetail.fileList.controls.${key}`, { defaultValue, ...options });
  const isDateSort = sortPref.sortKey === "uploaded_at";

  return (
    <section className="fileListControls" aria-label={text("title", "File list controls")}>
      <div className="fileListControls__heading">
        <div className="fileListControls__scope">
          <span>{text("scope", "Search scope")}</span>
          <strong>{scopeLabel || text("allFiles", "All project files")}</strong>
        </div>
        <button
          type="button"
          className={`fileListUploadToggle ${isUploadPanelOpen ? "isActive" : ""}`}
          onClick={onToggleUpload}
          disabled={uploadDisabled}
          aria-expanded={isUploadPanelOpen}
          aria-controls="project-file-upload-panel"
        >
          {isUploadPanelOpen ? <X size={16} aria-hidden="true" /> : <Upload size={16} aria-hidden="true" />}
          <span>{uploadToggleLabel}</span>
        </button>
      </div>

      {uploadPanel}

      <div className="fileList__toolbar fileListControls__query">
        <div className="fileListControls__sectionHeading">
          <strong>{text("queryTitle", "Find files")}</strong>
        </div>
        <div className="fileListControls__searchRow">
          <FileSearchBox
            layout="labeled"
            categoryLabel={text("searchCategory", "Search by")}
            keywordLabel={text("keyword", "Search term (automatic)")}
            categoryOptions={searchCategoryOptions}
            defaultCategoryKey="file_name"
            searchCategory={searchCategory}
            searchKeyword={searchKeyword}
            onSearchChange={onSearchChange}
            placeholderSuffix={t("projectDetail.fileList.search.placeholderSuffix", { defaultValue: "search" })}
          />
          <div className="fileListControlField fileListControls__filters">
            <span>{text("filter", "Filters")}</span>
            {detailFilterDropdown}
          </div>
          <button type="button" className="fileListControls__refresh" onClick={onRefresh} disabled={loading}>
            <RotateCw size={16} aria-hidden="true" />
            <span>{text("refresh", "Refresh")}</span>
          </button>
        </div>
        <div className="fileListControls__sortRow">
          <label className="fileListControlField fileListControlField--inline">
            <span>{text("sortKey", "Sort by")}</span>
            <select
              value={sortPref.sortKey}
              onChange={(event) => onSortChange({
                sortKey: event.target.value,
                sortDir: event.target.value === "uploaded_at" ? "DESC" : "ASC",
              })}
            >
              {FILE_SORT_PREF.FILE_SORT_KEYS.map((key) => (
                <option key={key} value={key}>{t(`projectDetail.fileList.${SORT_LABEL_KEYS[key]}`)}</option>
              ))}
            </select>
          </label>
          <label className="fileListControlField fileListControlField--inline">
            <span>{text("sortDirection", "Order")}</span>
            <select value={sortPref.sortDir} onChange={(event) => onSortChange({ ...sortPref, sortDir: event.target.value })}>
              <option value="DESC">{isDateSort ? text("newest", "Newest first") : text("descending", "Descending")}</option>
              <option value="ASC">{isDateSort ? text("oldest", "Oldest first") : text("ascending", "Ascending")}</option>
            </select>
          </label>
          <div className="fileListControls__viewOptions">
          <div className="fileListViewSwitch" role="group" aria-label={text("viewMode", "View mode")}>
            <button type="button" className={viewMode === "table" ? "isActive" : ""} aria-pressed={viewMode === "table"} onClick={() => onViewModeChange("table")}>
              <Table2 size={16} aria-hidden="true" />
              <span>{text("table", "Table")}</span>
            </button>
            <button type="button" className={viewMode === "card" ? "isActive" : ""} aria-pressed={viewMode === "card"} onClick={() => onViewModeChange("card")}>
              <LayoutGrid size={16} aria-hidden="true" />
              <span>{text("card", "Cards")}</span>
            </button>
          </div>
          {viewMode === "table" && (
            <details className="fileColumnSettings">
              <summary>
                <span>{text("columns", "Table columns")}</span>
                <ChevronDown size={16} aria-hidden="true" />
              </summary>
              <div className="fileColumnSettings__options">
                <p>{text("minimumColumns", "Keep at least {{count}} columns visible.", { count: MIN_FILE_LIST_COLUMNS })}</p>
                {FILE_LIST_COLUMNS.map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(key)}
                      disabled={visibleColumns.length <= MIN_FILE_LIST_COLUMNS && visibleColumns.includes(key)}
                      onChange={(event) => onToggleColumn(key, event.target.checked)}
                    />
                    {SORT_LABEL_KEYS[key] ? t(`projectDetail.fileList.${SORT_LABEL_KEYS[key]}`) : label}
                  </label>
                ))}
              </div>
            </details>
          )}
          </div>
          <span className="fileListControls__result" role="status" aria-atomic="true">
            {loading
              ? text("loading", "Loading files…")
              : error
                ? text("failed", "Unable to load files")
                : text("resultCount", "{{count}} files found", { count: totalCount })}
          </span>
        </div>
      </div>
      {selectionActions ? <div className="fileListControls__selection">{selectionActions}</div> : null}
    </section>
  );
}
